/**
 * AuditIQ Sports — Authenticated dashboards + admin tools
 * Single role-aware dashboard component drives all 11 role views.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import PortalLayout from "../../components/PortalLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Toaster } from "../../components/ui/sonner";
import { toast } from "sonner";
import { sportsApi } from "../../lib/sportsApi";
import { auth } from "../../lib/api";
import {
  Trophy, Activity, ShieldCheck, BarChart3, Users2, Target, AlertTriangle,
  Heart, Brain, GraduationCap, Briefcase, Database, Globe, FileText, Award,
  CheckCircle2, XCircle, ArrowRight, Crown, Stethoscope, Clipboard, Loader2,
  Eye, RefreshCw, Download, Play,
} from "lucide-react";

// ---------------- helpers ----------------
const SEV = {
  green:  { color: "text-[color:var(--auditiq-success)]", bg: "bg-[color:var(--auditiq-success)]/10", border: "border-[color:var(--auditiq-success)]/40" },
  yellow: { color: "text-[color:var(--auditiq-warning)]", bg: "bg-[color:var(--auditiq-warning)]/10", border: "border-[color:var(--auditiq-warning)]/40" },
  orange: { color: "text-[#F59E0B]",                       bg: "bg-[#F59E0B]/10",                     border: "border-[#F59E0B]/40" },
  red:    { color: "text-[color:var(--auditiq-critical)]", bg: "bg-[color:var(--auditiq-critical)]/10", border: "border-[color:var(--auditiq-critical)]/40" },
};
const ReadinessPill = ({ readiness, label }) => {
  const t = SEV[readiness] || SEV.yellow;
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${t.bg} ${t.color} ${t.border} border`}>
    <span className="pulse-dot" style={{ background: "currentColor" }} />{label || readiness}
  </span>;
};
const Stat = ({ label, value, accent = "gold" }) => (
  <div className="card-premium glass">
    <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{label}</div>
    <div className={`font-display text-3xl mt-1 text-${accent}`}>{value}</div>
  </div>
);
const Section = ({ title, children, testid }) => (
  <section className="mt-8" data-testid={testid}>
    <h2 className="font-display mb-4">{title}</h2>
    {children}
  </section>
);

// ---------------- ROLE → DASHBOARD CONFIG ----------------
const DASHBOARD_CONFIG = {
  "athletic-director":  { title: "Athletic Director Dashboard", Icon: Crown,        sections: ["overview", "teams", "healthSummary", "compliance", "reports"] },
  "head-coach":         { title: "Head Coach Dashboard",        Icon: Trophy,       sections: ["overview", "myTeam", "roster", "healthTeam", "recruiting"] },
  "assistant-coach":    { title: "Assistant Coach Dashboard",   Icon: Trophy,       sections: ["myTeam", "roster", "healthTeam"] },
  "position-coach":     { title: "Position Coach Dashboard",    Icon: Trophy,       sections: ["myTeam", "roster"] },
  "strength-coach":     { title: "Strength Coach Dashboard",    Icon: Activity,     sections: ["myTeam", "healthTeam"] },
  "trainer":            { title: "Trainer / Health Staff",      Icon: Stethoscope,  sections: ["healthTeam", "trainerQueue", "athleteCheckin"] },
  "advisor":            { title: "Academic Advisor Dashboard",  Icon: GraduationCap,sections: ["academic", "roster"] },
  "recruiting":         { title: "Recruiting Coordinator",      Icon: Users2,       sections: ["recruiting", "addProspect"] },
  "athlete":            { title: "Athlete Dashboard",           Icon: Activity,     sections: ["selfStats", "selfHealth", "checkin"] },
  "guardian":           { title: "Parent / Guardian Dashboard", Icon: Heart,        sections: ["childOverview", "childAcademic", "childHealthSafe"] },
  "sponsor":            { title: "Sponsor Viewer",              Icon: Award,        sections: ["aggregate"] },
  "compliance":         { title: "Compliance Officer",          Icon: ShieldCheck,  sections: ["complianceList", "audit"] },
  "auditor":            { title: "Read-Only Auditor",           Icon: Clipboard,    sections: ["audit", "reportsList"] },
  "admin":              { title: "Sports Admin",                Icon: Database,     sections: ["overview", "matchQueue", "crawlerSources", "leads"] },
};

// Path-alias → dashboard role mapping (URL aliases requested in spec §16)
const PATH_ALIAS_TO_ROLE = {
  "/auditiq-sports/admin": "admin",
  "/auditiq-sports/recruiting": "recruiting",
  "/auditiq-sports/performance": "head-coach",       // performance overview = head coach view
  "/auditiq-sports/reports": "auditor",              // reports listing = auditor view (read-only)
  "/auditiq-sports/athlete-dashboard": "athlete",
  "/auditiq-sports/coach-dashboard": "head-coach",
  "/auditiq-sports/athletic-director-dashboard": "athletic-director",
};

// Backend role (snake_case) → DASHBOARD_CONFIG key (kebab-case) mapping.
// Used when an authenticated user lands on /auditiq-sports/dashboard with no
// :role param and we resolve their dashboard from /api/auth/me.role.
const AUTH_ROLE_TO_DASHBOARD = {
  athletic_director:        "athletic-director",
  head_coach:               "head-coach",
  asst_coach:               "assistant-coach",
  assistant_coach:          "assistant-coach",
  position_coach:           "position-coach",
  strength_coach:           "strength-coach",
  trainer_health:           "trainer",
  trainer:                  "trainer",
  academic_advisor:         "advisor",
  recruiting_coordinator:   "recruiting",
  athlete:                  "athlete",
  parent_guardian:          "guardian",
  sponsor_viewer:           "sponsor",
  compliance_officer:       "compliance",
  read_only_auditor:        "auditor",
  university_admin:         "admin",
  super_admin:              "admin",
};

// =====================================================================
// MAIN — /auditiq-sports/dashboard/:role
// =====================================================================
export const SportsDashboard = () => {
  const { role: roleParam } = useParams();
  const [search] = useSearchParams();
  // Fallback chain: useParams → static path alias → authenticated user role → 'admin' default
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const [authRole, setAuthRole] = useState("");
  const mappedAuthRole = AUTH_ROLE_TO_DASHBOARD[authRole] || authRole;
  const role = roleParam || PATH_ALIAS_TO_ROLE[pathname] || mappedAuthRole || "";
  const cfg = DASHBOARD_CONFIG[role];
  const [user, setUser] = useState(null);
  const [unisInDemo, setUnisInDemo] = useState([]);
  const [selectedUni, setSelectedUni] = useState(search.get("u") || "fsu");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  // Detect signed-in user; many sections work without auth (using public + demo) but auth-enabled adds writes
  useEffect(() => {
    if (auth.token()) {
      auth.me().then(u => { setUser(u); setAuthRole(u?.role || ""); }).catch(() => setUser(null));
    }
    sportsApi.universities().then(d => setUnisInDemo(d.universities || []));
  }, []);

  // Load dashboard payload based on role
  useEffect(() => {
    if (!cfg) { setLoading(false); return; }
    setLoading(true);
    const u = selectedUni;
    const tasks = [];

    if (cfg.sections.includes("overview")) {
      tasks.push(sportsApi.university(u).then(r => ["uni", r]).catch(() => ["uni", null]));
      tasks.push(sportsApi.athletes({ university_id: u, limit: 200 }).then(r => ["athletes", r.athletes || []]).catch(() => ["athletes", []]));
    }
    if (cfg.sections.includes("teams") || cfg.sections.includes("myTeam")) {
      tasks.push(sportsApi.teams({ university_id: u }).then(r => ["teams", r.teams || []]).catch(() => ["teams", []]));
    }
    if (cfg.sections.includes("recruiting")) {
      tasks.push(sportsApi.prospects({ university_id: u }).then(r => ["prospects", r.prospects || []]).catch(() => ["prospects", []]));
    }
    if (cfg.sections.includes("matchQueue")) {
      tasks.push(sportsApi.matchQueue().then(r => ["queue", r.queue || []]).catch(() => ["queue", []]));
    }
    if (cfg.sections.includes("crawlerSources")) {
      tasks.push(sportsApi.crawlerSources().then(r => ["sources", r.sources || []]).catch(() => ["sources", []]));
      tasks.push(sportsApi.crawlerRuns().then(r => ["runs", r.runs || []]).catch(() => ["runs", []]));
    }
    if (cfg.sections.includes("complianceList") || cfg.sections.includes("compliance")) {
      tasks.push(sportsApi.compliance().then(r => ["compliance", r.records || []]).catch(() => ["compliance", []]));
    }
    if (cfg.sections.includes("reports") || cfg.sections.includes("reportsList")) {
      tasks.push(sportsApi.reports().then(r => ["reports", r]).catch(() => ["reports", { reports: [], report_types: [] }]));
    }
    if (cfg.sections.includes("leads")) {
      tasks.push(sportsApi.leads().then(r => ["leads", r.leads || []]).catch(() => ["leads", []]));
    }
    Promise.all(tasks).then(pairs => {
      const obj = Object.fromEntries(pairs);
      setData(obj);
      setLoading(false);
    });
  }, [role, selectedUni, cfg]);

  if (!cfg) {
    // No role yet (still resolving from auth.me) OR unrecognized role — show
    // friendly loading state rather than a hard "Unknown role" wall.
    if (auth.token() && !authRole) {
      return <PortalLayout title="Sports Dashboard">
        <div className="card-premium glass">Module loading — demo data available.</div>
      </PortalLayout>;
    }
    return <PortalLayout title="Sports Dashboard">
      <div className="card-premium glass">
        Unknown role: {role || "(none)"}.{" "}
        <Link to="/auditiq-sports/demo" className="text-electric">Go to demo</Link>
      </div>
    </PortalLayout>;
  }
  const Icon = cfg.Icon;

  return (
    <PortalLayout title={cfg.title}>
      <Toaster position="top-right" richColors theme="dark" />
      <div className="container-x section-y">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Icon size={22} className="text-gold" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-electric">AuditIQ Sports</div>
              <h1 className="font-display">{cfg.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedUni} onValueChange={setSelectedUni}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream w-64" data-testid="dashboard-uni-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {unisInDemo.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading && <div className="card-premium glass mt-6 flex items-center gap-3 text-cream-dim">
          <Loader2 size={18} className="animate-spin" /> Loading dashboard…
        </div>}

        {/* OVERVIEW (AD, Admin) */}
        {!loading && cfg.sections.includes("overview") && (
          <Section title="Overview" testid="dash-overview">
            <div className="grid sm:grid-cols-4 gap-4">
              <Stat label="Athletes" value={(data.athletes || []).length} />
              <Stat label="Teams" value={(data.teams || data.uni?.teams || []).length || 4} accent="electric" />
              <Stat label="Recruiting prospects" value={(data.prospects || []).length || (cfg.sections.includes("recruiting") ? 0 : 4)} />
              <Stat label="Sources" value={(data.sources || []).length || 8} accent="electric" />
            </div>
          </Section>
        )}

        {/* MY TEAM */}
        {!loading && cfg.sections.includes("myTeam") && (
          <Section title="Team(s) on file" testid="dash-myteam">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {(data.teams || []).map(t => (
                <Link key={t.id} to={`/auditiq-sports/team/${t.id}`}
                      className="glass p-4 hover:border-gold transition-colors block">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{t.sport}</div>
                  <div className="font-display text-cream mt-1">{t.name}</div>
                  <div className="text-electric text-xs mt-2">{t.head_coach}</div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {/* ROSTER (athletes for the selected university) */}
        {!loading && cfg.sections.includes("roster") && (
          <Section title="Roster" testid="dash-roster">
            <div className="table-wrap glass">
              <table className="w-full text-sm">
                <thead className="border-b border-gold-dim/20">
                  <tr className="text-cream-dim text-xs uppercase tracking-[0.18em]">
                    <th className="text-left p-3">Athlete</th>
                    <th className="text-left p-3">Sport</th>
                    <th className="text-left p-3">Pos</th>
                    <th className="text-right p-3">#</th>
                    <th className="text-left p-3">Class</th>
                    <th className="text-right p-3">GPA</th>
                    <th className="text-left p-3">Eligibility</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.athletes || []).slice(0, 30).map(a => (
                    <tr key={a.id} className="border-b border-gold-dim/10" data-testid={`roster-${a.id}`}>
                      <td className="p-3 text-cream">{a.first_name} {a.last_name}</td>
                      <td className="p-3 text-cream capitalize">{a.sport}</td>
                      <td className="p-3 text-cream">{a.position}</td>
                      <td className="p-3 text-right text-cream font-mono">{a.jersey}</td>
                      <td className="p-3 text-cream">{a.class_year}</td>
                      <td className="p-3 text-right text-cream">{a.gpa}</td>
                      <td className={`p-3 ${a.eligibility_status === "Eligible" ? "text-[color:var(--auditiq-success)]" : "text-[color:var(--auditiq-warning)]"}`}>
                        {a.eligibility_status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* HEALTH SUMMARY (AD-level, aggregate) */}
        {!loading && cfg.sections.includes("healthSummary") && (
          <HealthSummaryCard universityId={selectedUni} teams={data.teams || []} />
        )}

        {/* HEALTH TEAM (Trainer / Head Coach) */}
        {!loading && cfg.sections.includes("healthTeam") && (
          <HealthTeamCard universityId={selectedUni} teams={data.teams || []} />
        )}

        {/* TRAINER QUEUE */}
        {!loading && cfg.sections.includes("trainerQueue") && (
          <TrainerQueueCard universityId={selectedUni} teams={data.teams || []} />
        )}

        {/* ATHLETE-FACING CHECK-IN */}
        {!loading && cfg.sections.includes("athleteCheckin") && (
          <AthleteCheckinCard userAthleteId={user?.athlete_id} />
        )}

        {/* SELF STATS + SELF HEALTH (Athlete only) */}
        {!loading && cfg.sections.includes("selfStats") && (
          <AthleteSelfCards athleteId={user?.athlete_id || "ath-fsu-football-01"} />
        )}
        {!loading && cfg.sections.includes("checkin") && (
          <AthleteCheckinCard userAthleteId={user?.athlete_id || "ath-fsu-football-01"} userRole={user?.role} />
        )}

        {/* CHILD VIEWS (Guardian) */}
        {!loading && cfg.sections.includes("childOverview") && (
          <GuardianChildCards athleteId={user?.athlete_id || "ath-fsu-football-01"} />
        )}

        {/* RECRUITING */}
        {!loading && cfg.sections.includes("recruiting") && (
          <RecruitingCard prospects={data.prospects || []} universityId={selectedUni} reload={() => sportsApi.prospects({ university_id: selectedUni }).then(r => setData(d => ({ ...d, prospects: r.prospects || [] })))} />
        )}

        {/* COMPLIANCE LIST */}
        {!loading && (cfg.sections.includes("complianceList") || cfg.sections.includes("compliance")) && (
          <ComplianceCard records={data.compliance || []} />
        )}

        {/* AUDIT (auditor, compliance) */}
        {!loading && cfg.sections.includes("audit") && (
          <Section title="Recent compliance + audit activity" testid="dash-audit">
            <div className="card-premium glass">
              <p className="text-cream-dim text-sm">
                Audit logs are sourced from the central AuditIQ audit log stream.
                Open the Admin Hub for full filtering.
              </p>
              <Link to="/portal/audit-logs" className="text-electric text-sm mt-3 inline-flex items-center gap-1">
                Open Audit Log Viewer <ArrowRight size={12} />
              </Link>
            </div>
          </Section>
        )}

        {/* REPORTS (AD, Admin, Auditor) */}
        {!loading && (cfg.sections.includes("reports") || cfg.sections.includes("reportsList")) && (
          <ReportsCard payload={data.reports || { reports: [], report_types: [] }}
                       universityId={selectedUni}
                       canGenerate={cfg.sections.includes("reports")} />
        )}

        {/* SPONSOR AGGREGATE */}
        {!loading && cfg.sections.includes("aggregate") && (
          <Section title="Sponsor-safe aggregate impact" testid="dash-sponsor">
            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label="Athletes engaged" value={(data.athletes || []).length || 80} />
              <Stat label="Universities" value={unisInDemo.length} accent="electric" />
              <Stat label="Programs" value="40" />
            </div>
            <div className="card-premium glass mt-4 border-l-2 border-gold">
              <p className="text-cream-dim text-sm">
                Sponsor view contains only aggregated, sponsor-safe metrics — no
                private athlete, medical, or academic information is exposed.
              </p>
            </div>
          </Section>
        )}

        {/* MATCH QUEUE (Admin) */}
        {!loading && cfg.sections.includes("matchQueue") && (
          <MatchQueueCard queue={data.queue || []}
                          reload={() => sportsApi.matchQueue().then(r => setData(d => ({ ...d, queue: r.queue || [] })))} />
        )}

        {/* CRAWLER SOURCES (Admin) */}
        {!loading && cfg.sections.includes("crawlerSources") && (
          <CrawlerCard sources={data.sources || []} runs={data.runs || []}
                       reload={() => Promise.all([sportsApi.crawlerSources(), sportsApi.crawlerRuns()])
                          .then(([s, r]) => setData(d => ({ ...d, sources: s.sources || [], runs: r.runs || [] })))} />
        )}

        {/* LEADS (Admin) */}
        {!loading && cfg.sections.includes("leads") && (
          <Section title="Pilot demo leads" testid="dash-leads">
            <div className="card-premium glass">
              <div className="text-cream-dim text-sm mb-3">{data.leads?.length || 0} leads</div>
              <div className="space-y-2">
                {(data.leads || []).slice(0, 10).map(l => (
                  <div key={l.id} className="flex justify-between items-center border-b border-gold-dim/10 py-2 text-sm">
                    <div className="text-cream">{l.contact_name} · {l.organization}</div>
                    <div className="text-electric text-xs font-mono">{l.email}</div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ACADEMIC (Advisor) */}
        {!loading && cfg.sections.includes("academic") && (
          <Section title="Academic overview" testid="dash-academic">
            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label="Athletes" value={(data.athletes || []).length} />
              <Stat label="GPA ≥ 3.5" value={(data.athletes || []).filter(a => a.gpa >= 3.5).length} accent="electric" />
              <Stat label="Eligibility review" value={(data.athletes || []).filter(a => a.eligibility_status === "Review").length} />
            </div>
          </Section>
        )}
      </div>
    </PortalLayout>
  );
};

// ---------------- SUB-CARDS ----------------

const HealthSummaryCard = ({ universityId, teams }) => {
  const [summary, setSummary] = useState({ green: 0, yellow: 0, orange: 0, red: 0 });
  useEffect(() => {
    if (!teams.length) return;
    Promise.all(teams.map(t => sportsApi.hiqTeam(t.id).catch(() => null))).then(rows => {
      const sum = { green: 0, yellow: 0, orange: 0, red: 0 };
      rows.filter(Boolean).forEach(r => {
        sum.green += r.summary.green; sum.yellow += r.summary.yellow;
        sum.orange += r.summary.orange; sum.red += r.summary.red;
      });
      setSummary(sum);
    });
  }, [teams]);
  return (
    <Section title="Health IQ readiness summary" testid="dash-health-summary">
      <div className="grid sm:grid-cols-4 gap-4" data-testid="hiq-summary">
        <Stat label="Ready (Green)" value={summary.green} accent="[color:var(--auditiq-success)]" />
        <div className="card-premium glass"><div className="text-[10px] uppercase tracking-[0.22em] text-steel">Monitor (Yellow)</div><div className="font-display text-3xl mt-1 text-[color:var(--auditiq-warning)]">{summary.yellow}</div></div>
        <div className="card-premium glass"><div className="text-[10px] uppercase tracking-[0.22em] text-steel">Limited (Orange)</div><div className="font-display text-3xl mt-1 text-[#F59E0B]">{summary.orange}</div></div>
        <div className="card-premium glass"><div className="text-[10px] uppercase tracking-[0.22em] text-steel">Hold (Red)</div><div className="font-display text-3xl mt-1 text-[color:var(--auditiq-critical)]">{summary.red}</div></div>
      </div>
    </Section>
  );
};

const HealthTeamCard = ({ universityId, teams }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [team, setTeam] = useState(null);
  // Set initial team only — intentionally not depending on selectedTeam to avoid re-trigger after manual selection
  useEffect(() => { if (teams.length && !selectedTeam) setSelectedTeam(teams[0].id); }, [teams]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedTeam) sportsApi.hiqTeam(selectedTeam).then(setTeam).catch(() => setTeam(null)); }, [selectedTeam]);
  return (
    <Section title="Team Health IQ" testid="dash-health-team">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <Select value={selectedTeam || ""} onValueChange={setSelectedTeam}>
          <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream w-72"><SelectValue placeholder="Select team" /></SelectTrigger>
          <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
            {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {team && (
        <>
          <div className="grid sm:grid-cols-4 gap-3 mb-4">
            <div className="glass p-3 text-center"><div className="text-[10px] text-steel">Green</div><div className="font-display text-2xl text-[color:var(--auditiq-success)]">{team.summary.green}</div></div>
            <div className="glass p-3 text-center"><div className="text-[10px] text-steel">Yellow</div><div className="font-display text-2xl text-[color:var(--auditiq-warning)]">{team.summary.yellow}</div></div>
            <div className="glass p-3 text-center"><div className="text-[10px] text-steel">Orange</div><div className="font-display text-2xl text-[#F59E0B]">{team.summary.orange}</div></div>
            <div className="glass p-3 text-center"><div className="text-[10px] text-steel">Red</div><div className="font-display text-2xl text-[color:var(--auditiq-critical)]">{team.summary.red}</div></div>
          </div>
          <div className="card-premium glass-accent flex items-start gap-3 mb-4">
            <AlertTriangle size={16} className="text-electric mt-0.5 shrink-0" />
            <div className="text-cream-dim text-xs">{team.disclaimer}</div>
          </div>
          <div className="space-y-2">
            {team.checkins.map(c => (
              <div key={c.id || c.athlete_id} className="glass p-3 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-cream font-medium">#{c.jersey} {c.athlete_name}</div>
                  <div className="text-cream-dim text-xs">{c.position} · score {c.score}</div>
                </div>
                <ReadinessPill readiness={c.readiness} label={c.readiness_label} />
              </div>
            ))}
          </div>
        </>
      )}
    </Section>
  );
};

const TrainerQueueCard = ({ universityId, teams }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!teams.length) return;
    Promise.all(teams.map(t => sportsApi.hiqTeam(t.id).catch(() => null))).then(rows => {
      const need = [];
      rows.filter(Boolean).forEach(r => r.checkins.filter(c => c.trainer_review_required).forEach(c => need.push({ ...c, team: r.team.name })));
      setItems(need);
    });
  }, [teams]);
  return (
    <Section title="Trainer review queue" testid="trainer-queue">
      {items.length === 0 ? <div className="card-premium glass text-cream-dim text-sm">No items pending review.</div> :
        <div className="space-y-2">
          {items.map(c => (
            <div key={c.id} className="glass p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`trainer-item-${c.id}`}>
              <div>
                <div className="text-cream">#{c.jersey} {c.athlete_name} · {c.team}</div>
                <div className="text-cream-dim text-xs">Score {c.score} · pain {c.pain}</div>
              </div>
              <div className="flex items-center gap-2">
                <ReadinessPill readiness={c.readiness} label={c.readiness_label} />
                <Button onClick={async () => {
                  const note = prompt("Trainer note:", "Reviewed. Light practice approved.");
                  if (!note) return;
                  try {
                    await sportsApi.hiqReview(c.id, { trainer_notes: note, cleared: true });
                    toast.success("Reviewed and cleared");
                    setItems(items.filter(x => x.id !== c.id));
                  } catch (e) { toast.error("Review failed"); }
                }} className="btn-gold h-9 text-xs">Mark reviewed</Button>
              </div>
            </div>
          ))}
        </div>}
    </Section>
  );
};

const AthleteCheckinCard = ({ userAthleteId, userRole }) => {
  const [form, setForm] = useState({ sleep_hours: 7, energy: 7, soreness: 3, stress: 4, hydration: 7, pain: 1, notes: "" });
  const [athleteIdField, setAthleteIdField] = useState(userAthleteId || "");
  const [submitting, setSubmitting] = useState(false);
  const [last, setLast] = useState(null);
  // Keep field in sync if athlete logs in later (user object loads async)
  useEffect(() => { if (userAthleteId) setAthleteIdField(userAthleteId); }, [userAthleteId]);
  const submit = async (e) => {
    e.preventDefault();
    const targetId = userAthleteId || athleteIdField;
    if (!targetId) { toast.error("Athlete ID required — sign in as athlete or trainer"); return; }
    setSubmitting(true);
    try {
      const r = await sportsApi.hiqCheckin({ athlete_id: targetId, ...form });
      setLast(r); toast.success(`Submitted · ${r.readiness_label} (${r.score})`);
    } catch (err) { toast.error("Check-in failed — sign in required"); }
    finally { setSubmitting(false); }
  };
  const isAthlete = userRole === "athlete";
  return (
    <Section title="Daily Health IQ check-in" testid="hiq-checkin">
      <form onSubmit={submit} className="card-premium glass grid sm:grid-cols-2 gap-3" data-testid="hiq-checkin-form">
        <label className="block sm:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-1">Athlete ID {isAthlete && <span className="text-electric">(your own)</span>}</div>
          <Input
            name="athlete_id"
            value={athleteIdField}
            onChange={e => !isAthlete && setAthleteIdField(e.target.value)}
            disabled={isAthlete}
            className="bg-navy-deep border-gold-dim/40 text-cream font-mono text-xs"
            data-testid="hiq-athlete-id" />
        </label>
        {[
          ["sleep_hours","Sleep (hours)",0,14,0.5],
          ["energy","Energy (1-10)",1,10,1],
          ["soreness","Soreness (1-10)",1,10,1],
          ["stress","Stress (1-10)",1,10,1],
          ["hydration","Hydration (1-10)",1,10,1],
          ["pain","Pain (0-10)",0,10,1],
        ].map(([k,label,min,max,step]) => (
          <label key={k} className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-steel mb-1">{label}</div>
            <Input type="number" min={min} max={max} step={step} value={form[k]}
                   onChange={e => setForm({ ...form, [k]: Number(e.target.value) })}
                   className="bg-navy-deep border-gold-dim/40" data-testid={`hiq-${k}`} />
          </label>
        ))}
        <Textarea placeholder="Notes (optional)" rows={2}
                  value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="bg-navy-deep border-gold-dim/40 text-cream sm:col-span-2" />
        <div className="sm:col-span-2 flex justify-end">
          <Button type="submit" disabled={submitting} className="btn-gold h-12" data-testid="hiq-submit">
            {submitting && <Loader2 size={14} className="animate-spin mr-2" />}Submit Check-in
          </Button>
        </div>
      </form>
      {last && <div className="card-premium glass mt-4">
        <div className="flex items-center justify-between">
          <div className="text-cream">Latest readiness</div>
          <ReadinessPill readiness={last.readiness} label={last.readiness_label} />
        </div>
        <div className="text-cream-dim text-xs mt-2">Score {last.score}</div>
      </div>}
    </Section>
  );
};

const AthleteSelfCards = ({ athleteId }) => {
  const [stats, setStats] = useState(null);
  const [hiq, setHiq] = useState(null);
  const [athlete, setAthlete] = useState(null);
  useEffect(() => {
    if (!athleteId) return;
    sportsApi.athlete(athleteId).then(setAthlete).catch(() => {});
    sportsApi.athleteStats(athleteId).then(setStats).catch(() => {});
    sportsApi.athleteHealth(athleteId).then(d => setHiq((d.checkins || [])[0])).catch(() => {});
  }, [athleteId]);
  return (
    <Section title="My profile" testid="athlete-self">
      <div className="grid sm:grid-cols-3 gap-4">
        {athlete && <div className="card-premium glass">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Profile</div>
          <div className="font-display text-cream mt-1">{athlete.first_name} {athlete.last_name}</div>
          <div className="text-electric text-xs mt-2 capitalize">{athlete.sport} · {athlete.position} · #{athlete.jersey}</div>
          <div className="text-cream-dim text-xs mt-2">GPA {athlete.gpa} · {athlete.eligibility_status}</div>
        </div>}
        {hiq && <div className="card-premium glass">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Health IQ</div>
          <div className="mt-2"><ReadinessPill readiness={hiq.readiness} label={hiq.readiness_label} /></div>
          <div className="text-cream-dim text-xs mt-2">Score {hiq.score}</div>
        </div>}
        {stats && <div className="card-premium glass">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Season</div>
          <div className="font-display text-cream mt-1 capitalize">{stats.sport} · {stats.season}</div>
          <div className="text-cream-dim text-xs mt-2">Practice grade {stats.practice_grade} · Game grade {stats.game_grade}</div>
        </div>}
      </div>
    </Section>
  );
};

const GuardianChildCards = ({ athleteId }) => {
  const [athlete, setAthlete] = useState(null);
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    if (!athleteId) return;
    sportsApi.athlete(athleteId).then(setAthlete).catch(() => {});
    sportsApi.athleteHealth(athleteId).then(d => setLatest((d.checkins || [])[0])).catch(() => setLatest(null));
  }, [athleteId]);
  return (
    <Section title="My athlete" testid="guardian-child">
      <div className="grid sm:grid-cols-2 gap-4">
        {athlete && <div className="card-premium glass">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Profile</div>
          <div className="font-display text-cream mt-1">{athlete.first_name} {athlete.last_name}</div>
          <div className="text-cream-dim text-xs mt-2 capitalize">{athlete.sport} · {athlete.position}</div>
          <div className="text-cream-dim text-xs mt-1">GPA {athlete.gpa} · {athlete.eligibility_status}</div>
        </div>}
        <div className="card-premium glass">
          <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Approved Health IQ summary</div>
          {latest ? <>
            <div className="mt-2"><ReadinessPill readiness={latest.readiness} label={latest.readiness_label} /></div>
            <div className="text-cream-dim text-xs mt-2">Sleep & private fields are not shared with this view.</div>
          </> : <div className="text-cream-dim text-sm mt-2">No approved summary available.</div>}
        </div>
      </div>
    </Section>
  );
};

const RecruitingCard = ({ prospects, universityId, reload }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", university_id: universityId, sport: "football", position: "QB", hs_school: "", hometown: "", class_year: 2026, rating_stars: 3, stage: "Identified" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await sportsApi.addProspect({ ...form, university_id: universityId });
      toast.success("Prospect added"); setOpen(false); reload();
    } catch (err) { toast.error("Add failed"); }
  };
  return (
    <Section title={`Recruiting pipeline (${prospects.length})`} testid="dash-recruiting">
      <div className="flex justify-end mb-3">
        <Button onClick={() => setOpen(o => !o)} className="btn-ghost-gold h-10" data-testid="recruit-toggle-add">
          {open ? "Cancel" : "+ Add prospect"}
        </Button>
      </div>
      {open && (
        <form onSubmit={submit} className="card-premium glass grid sm:grid-cols-2 gap-3 mb-4">
          <Input required placeholder="First name" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <Input required placeholder="Last name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <Select value={form.sport} onValueChange={v => setForm({ ...form, sport: v })}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {["football","basketball","baseball","golf"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Position" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="High school" value={form.hs_school} onChange={e => setForm({ ...form, hs_school: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="Hometown" value={form.hometown} onChange={e => setForm({ ...form, hometown: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <div className="sm:col-span-2 flex justify-end"><Button className="btn-gold h-11" type="submit" data-testid="recruit-submit">Add prospect</Button></div>
        </form>
      )}
      <div className="table-wrap glass">
        <table className="w-full text-sm">
          <thead className="border-b border-gold-dim/20"><tr className="text-cream-dim text-xs uppercase tracking-[0.18em]">
            <th className="text-left p-3">Name</th><th className="text-left p-3">Sport</th><th className="text-left p-3">Pos</th>
            <th className="text-right p-3">Stars</th><th className="text-left p-3">Stage</th><th className="text-right p-3">AI Fit</th>
          </tr></thead>
          <tbody>
            {prospects.slice(0, 25).map(p => (
              <tr key={p.id} className="border-b border-gold-dim/10" data-testid={`prospect-${p.id}`}>
                <td className="p-3 text-cream">{p.first_name} {p.last_name}</td>
                <td className="p-3 text-cream capitalize">{p.sport}</td>
                <td className="p-3 text-cream">{p.position}</td>
                <td className="p-3 text-right text-gold">{"★".repeat(p.rating_stars || 3)}</td>
                <td className="p-3 text-electric">{p.stage}</td>
                <td className="p-3 text-right text-cream">{p.ai_fit_score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

const ComplianceCard = ({ records }) => (
  <Section title="Compliance records" testid="dash-compliance">
    <div className="table-wrap glass">
      <table className="w-full text-sm">
        <thead className="border-b border-gold-dim/20"><tr className="text-cream-dim text-xs uppercase tracking-[0.18em]">
          <th className="text-left p-3">Type</th><th className="text-left p-3">Athlete</th>
          <th className="text-left p-3">Status</th><th className="text-left p-3">Summary</th>
        </tr></thead>
        <tbody>
          {records.map(r => (
            <tr key={r.id} className="border-b border-gold-dim/10" data-testid={`compliance-${r.id}`}>
              <td className="p-3 text-cream">{r.type}</td>
              <td className="p-3 text-cream font-mono text-xs">{r.athlete_id}</td>
              <td className={`p-3 ${r.status === "Compliant" ? "text-[color:var(--auditiq-success)]" :
                r.status === "Action Required" ? "text-[color:var(--auditiq-critical)]" : "text-[color:var(--auditiq-warning)]"}`}>{r.status}</td>
              <td className="p-3 text-cream-dim">{r.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Section>
);

const ReportsCard = ({ payload, universityId, canGenerate }) => {
  const [type, setType] = useState("team_weekly");
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState(payload.reports || []);
  const types = payload.report_types || [];
  const generate = async () => {
    setGenerating(true);
    try {
      const r = await sportsApi.generateReport({ report_type: type, university_id: universityId, sport: "football" });
      setReports([r, ...reports]);
      toast.success(`Generated · ${r.title}`);
    } catch (err) { toast.error("Generate failed"); }
    finally { setGenerating(false); }
  };
  const view = async (rid) => {
    const r = await sportsApi.downloadReport(rid).catch(() => null);
    if (!r) return toast.error("Download failed");
    // Iter 19c security fix — render raw markdown in a Blob URL with text/plain MIME
    // so the browser treats it as inert text. Avoids `document.write` and any HTML
    // interpolation of the report content.
    try {
      const blob = new Blob([String(r.content_markdown || "")], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Free the URL after the new tab has had a chance to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Could not open report");
    }
  };
  // Direct binary download (PDF/CSV) — uses authed fetch so the JWT is sent.
  const downloadBinary = async (rid, fmt) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/sports/reports/${rid}/download?fmt=${fmt}`;
      const token = localStorage.getItem("auditiq_token");
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj; a.download = `${rid}.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(obj), 1000);
    } catch (err) { toast.error(`${fmt.toUpperCase()} download failed`); }
  };
  return (
    <Section title="Reports" testid="dash-reports">
      {canGenerate && (
        <div className="card-premium glass flex items-center gap-3 flex-wrap mb-4">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream w-72" data-testid="report-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={generate} disabled={generating} className="btn-gold h-12" data-testid="report-generate">
            {generating ? <Loader2 size={14} className="animate-spin mr-2" /> : <FileText size={14} className="mr-2" />}
            Generate report
          </Button>
        </div>
      )}
      <div className="space-y-2">
        {reports.length === 0 && <div className="card-premium glass text-cream-dim text-sm">No reports yet — generate one above.</div>}
        {reports.map(r => (
          <div key={r.id} className="glass p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`report-${r.id}`}>
            <div>
              <div className="text-cream font-medium">{r.title}</div>
              <div className="text-cream-dim text-xs mt-1">{r.university_name || "All"} · {(r.sport || "All").toUpperCase()} · {new Date(r.created_at).toLocaleString()}</div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => view(r.id)} className="btn-ghost-gold h-9 text-xs" data-testid={`report-view-${r.id}`}>
                <Eye size={12} className="mr-2" /> View
              </Button>
              <Button onClick={() => downloadBinary(r.id, "pdf")} className="btn-ghost-gold h-9 text-xs" data-testid={`report-pdf-${r.id}`}>
                <Download size={12} className="mr-2" /> PDF
              </Button>
              <Button onClick={() => downloadBinary(r.id, "csv")} className="btn-ghost-gold h-9 text-xs" data-testid={`report-csv-${r.id}`}>
                <Download size={12} className="mr-2" /> CSV
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const MatchQueueCard = ({ queue, reload }) => {
  const decide = async (id, action) => {
    try {
      const fn = action === "approve" ? sportsApi.matchApprove
              : action === "reject"  ? sportsApi.matchReject
              :                         sportsApi.matchMerge;
      await fn(id, { notes: "" });
      toast.success(`Match ${action}d`);
      reload();
    } catch (err) { toast.error("Action failed"); }
  };
  return (
    <Section title={`Athlete match approval queue (${queue.length})`} testid="match-queue">
      <div className="text-cream-dim text-sm mb-3">
        Candidates with confidence 70-89% require human approval. Above 90 auto-merges; below 70 stays unresolved.
      </div>
      <div className="space-y-3">
        {queue.length === 0 && <div className="card-premium glass text-cream-dim text-sm">Queue empty.</div>}
        {queue.map(c => (
          <div key={c.id} className="glass p-4 grid lg:grid-cols-[1fr_auto] gap-3 items-start" data-testid={`match-${c.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-display text-cream">{c.candidate_name}</div>
                <span className={`text-xs px-2 py-0.5 rounded ${c.confidence >= 80 ? "bg-[color:var(--auditiq-warning)]/15 text-[color:var(--auditiq-warning)]" : "bg-[color:var(--auditiq-critical)]/15 text-[color:var(--auditiq-critical)]"}`}>{c.confidence}% confidence</span>
                <span className={`text-xs px-2 py-0.5 rounded ${c.status === "pending" ? "bg-electric/15 text-electric" : "bg-cream-dim/10 text-cream-dim"}`}>{c.status}</span>
              </div>
              <div className="text-cream-dim text-xs mt-2">Possible match: {c.possible_existing_athlete}</div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-electric mb-1">Matching evidence</div>
                  <ul className="text-cream-dim text-xs space-y-1">{c.evidence_match.map((e, i) => <li key={i}>• {e}</li>)}</ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--auditiq-critical)] mb-1">Conflicting evidence</div>
                  <ul className="text-cream-dim text-xs space-y-1">
                    {c.evidence_conflict.length ? c.evidence_conflict.map((e, i) => <li key={i}>• {e}</li>) : <li>None</li>}
                  </ul>
                </div>
              </div>
              <div className="text-[10px] text-cream-dim mt-2">Sources: {c.source_urls.join(" · ")}</div>
            </div>
            {c.status === "pending" && (
              <div className="flex flex-col gap-2 lg:w-44">
                <Button onClick={() => decide(c.id, "approve")} className="btn-gold h-9 text-xs" data-testid={`match-approve-${c.id}`}>
                  <CheckCircle2 size={12} className="mr-2" /> Approve
                </Button>
                <Button onClick={() => decide(c.id, "merge")} className="btn-ghost-gold h-9 text-xs" data-testid={`match-merge-${c.id}`}>
                  <RefreshCw size={12} className="mr-2" /> Merge
                </Button>
                <Button onClick={() => decide(c.id, "reject")} className="btn-ghost-gold h-9 text-xs" data-testid={`match-reject-${c.id}`}>
                  <XCircle size={12} className="mr-2" /> Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
};

const CrawlerCard = ({ sources, runs, reload }) => {
  const run = async (sid) => {
    try {
      const r = await sportsApi.crawlerRun(sid);
      toast.success(`Crawled · ${r.records_in} records`);
      reload();
    } catch (err) { toast.error("Crawl failed"); }
  };
  return (
    <Section title="Crawler sources + runs" testid="crawler-card">
      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div>
          <h3 className="font-display mb-3 text-base">Sources ({sources.length})</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {sources.map(s => (
              <div key={s.id} className="glass p-3 flex items-center justify-between gap-2" data-testid={`source-${s.id}`}>
                <div>
                  <div className="text-cream text-sm">{s.name}</div>
                  <div className="text-cream-dim text-xs">{s.source_type} · {s.crawl_frequency} · {s.robots_status}</div>
                </div>
                <Button onClick={() => run(s.id)} className="btn-ghost-gold h-8 text-xs" data-testid={`source-run-${s.id}`}>
                  <Play size={10} className="mr-1" /> Run
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-display mb-3 text-base">Recent runs ({runs.length})</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {runs.slice(0, 30).map(r => (
              <div key={r.id} className="glass p-3" data-testid={`run-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-cream text-sm">{r.source_name}</div>
                  <span className={`text-xs px-2 py-0.5 rounded ${r.status === "completed" ? "bg-[color:var(--auditiq-success)]/15 text-[color:var(--auditiq-success)]" : "bg-[color:var(--auditiq-critical)]/15 text-[color:var(--auditiq-critical)]"}`}>{r.status}</span>
                </div>
                <div className="text-cream-dim text-xs mt-1">In: {r.records_in} · Normalized: {r.records_normalized} · Errors: {r.errors}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

// =====================================================================
// SPORTS ADMIN — /auditiq-sports/admin (alias for dashboard/admin)
// =====================================================================
export const SportsAdmin = SportsDashboard;

export default SportsDashboard;
